import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, Button, FormGroup, Label, Input, Form } from 'reactstrap';
import RhAdministracionService from '../../axios_services/rh_administracion.service';
import ArchivosService from '../../axios_services/archivos.service';
import Loader from '../Loader/Loader';
import { AppContext } from '../../application/provider';

const formatSoles = (amount) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount || 0);

const calcImporteNeto = (f) => {
    const pago = parseFloat(f.pago_mes) || 0;
    const bono = parseFloat(f.bono_gestion) || 0;
    const desc = parseFloat(f.descuento) || 0;
    const detr = parseFloat(f.detraccion) || 0;
    return parseFloat(((pago - desc) + bono - detr).toFixed(2));
};

const ModalRegistroPagoRhAdministracion = ({ isOpen, onClose, onRefresh, item, periodo }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const empresaOptions = state?.catalogos?.tipos?.filter(t => Number(t.categoria_id) === 37) || [];

    const [formData, setFormData] = useState({
        area: '', apellidos_nombre: '', cargo: '', empresa_id: '', rxh_factura: '',
        pago_mes: 0, bono_gestion: 0, descuento: 0, detraccion: 0,
        dni: '', n_cuenta: '', cci: '', banco_pago: '',
        n_cuenta_detraccion: '', banco_detraccion: ''
    });

    const [boletaFile, setBoletaFile] = useState(null);
    const fileRef = React.useRef(null);

    const userProfile = Number(state.user?.perfil_id);
    const canUseActions = [5, 8, 22, 26].includes(userProfile);

    useEffect(() => {
        if (isOpen && item) {
            setFormData({
                area: item.area || '', apellidos_nombre: item.apellidos_nombre || '',
                cargo: item.cargo || '', empresa_id: item.empresa_id || '', rxh_factura: item.rxh_factura || '',
                pago_mes: item.pago_mes || 0, bono_gestion: item.bono_gestion || 0, descuento: item.descuento || 0,
                detraccion: item.detraccion || 0, dni: item.dni || '', n_cuenta: item.n_cuenta || '',
                cci: item.cci || '', banco_pago: item.banco_pago || '',
                n_cuenta_detraccion: item.n_cuenta_detraccion || '', banco_detraccion: item.banco_detraccion || ''
            });
            setBoletaFile(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [isOpen, item]);

    if (!item) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleActualizar = async () => {
        setIsLoading(true);
        try {
            const payload = {
                rh_administracion_id: item.rh_administracion_id,
                area: formData.area,
                apellidos_nombre: formData.apellidos_nombre,
                cargo: formData.cargo,
                empresa_id: formData.empresa_id,
                rxh_factura: formData.rxh_factura,
                pago_mes: formData.pago_mes,
                bono_gestion: formData.bono_gestion,
                descuento: formData.descuento,
                detraccion: formData.detraccion,
                dni: formData.dni,
                n_cuenta: formData.n_cuenta,
                cci: formData.cci,
                banco_pago: formData.banco_pago,
                n_cuenta_detraccion: formData.n_cuenta_detraccion,
                banco_detraccion: formData.banco_detraccion,
                importe_neto: calcImporteNeto(formData)
            };

            let insertedId = item.rh_administracion_id;

            if (insertedId) {
                await RhAdministracionService.actualizarPago(payload);
            } else {
                const response = await RhAdministracionService.guardarPlanillaRh({
                    periodo_planilla: periodo,
                    registros: [payload]
                });
                // We don't get the new ID easily here from a mass insert route without modifying backend,
                // but fetchDatos() will reload all data with their new IDs anyway!
            }
            
            if (boletaFile && canUseActions) {
                try {
                    // Note: if insertedId is null, we can't save boletaFile because we don't have the new ID.
                    if (!insertedId) {
                        alert('El registro se guardó. Para subir la boleta, por favor actualiza nuevamente este registro ya guardado.');
                    } else {
                        await ArchivosService.guardarBoletaPago(boletaFile, insertedId, periodo, state.user.usuario_id);
                    }
                } catch (uploadError) {
                    const errorMsg = uploadError.response?.data?.message || '';
                    if (errorMsg.includes('failed to upload')) {
                        throw new Error('El archivo PDF pesa demasiado. (límite 2MB).');
                    }
                    throw uploadError;
                }
            }

            alert('Registro y boleta actualizados correctamente.');
            if (onRefresh) onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
            alert(e.message || 'Error al actualizar el pago. Inténtelo nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>ACTUALIZAR REGISTRO PLANILLA RH</h1>
                    <button className="close-btn" onClick={onClose}>X</button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}
                    <Form onSubmit={(e) => { e.preventDefault(); handleActualizar(); }} className="px-4">
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Área:</Label>
                            <Input type="text" name="area" value={formData.area} onChange={handleChange} disabled={!canUseActions} placeholder="Ingresa el área" />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Apellidos y Nombre:</Label>
                            <Input type="text" name="apellidos_nombre" value={formData.apellidos_nombre} onChange={handleChange} disabled={!canUseActions} placeholder="Ingresa los apellidos y nombre" />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Cargo:</Label>
                            <Input type="text" name="cargo" value={formData.cargo} onChange={handleChange} disabled={!canUseActions} placeholder="Ingresa el cargo" />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Empresa:</Label>
                            <Input type="select" name="empresa_id" value={formData.empresa_id} onChange={handleChange} disabled={!canUseActions}>
                                <option value="">Seleccionar empresa...</option>
                                {empresaOptions.map(opt => (
                                    <option key={opt.tipo_id} value={opt.tipo_id}>{(opt.descripcion || opt.nombre || '').toUpperCase()}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">RxH / Factura:</Label>
                            <Input type="text" name="rxh_factura" value={formData.rxh_factura} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Pago Mes (S/):</Label>
                            <Input type="number" step="0.01" name="pago_mes" value={formData.pago_mes} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Bono Gestión (S/):</Label>
                            <Input type="number" step="0.01" name="bono_gestion" value={formData.bono_gestion} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Descuento (S/):</Label>
                            <Input type="number" step="0.01" name="descuento" value={formData.descuento} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Detracción (S/):</Label>
                            <Input type="number" step="0.01" name="detraccion" value={formData.detraccion} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Importe Neto a Pagar:</Label>
                            <Input type="text" value={formatSoles(calcImporteNeto(formData))} disabled className="fw-bold" />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">DNI:</Label>
                            <Input type="text" name="dni" value={formData.dni} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">N° Cuenta:</Label>
                            <Input type="text" name="n_cuenta" value={formData.n_cuenta} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">CCI:</Label>
                            <Input type="text" name="cci" value={formData.cci} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Banco:</Label>
                            <Input type="text" name="banco_pago" value={formData.banco_pago} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Cuenta Detracción:</Label>
                            <Input type="text" name="n_cuenta_detraccion" value={formData.n_cuenta_detraccion} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Banco Detracción:</Label>
                            <Input type="text" name="banco_detraccion" value={formData.banco_detraccion} onChange={handleChange} disabled={!canUseActions} />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label>Subir/Actualizar Boleta de Pago (PDF):</Label>
                            <div className="cap-upload-zone">
                                <span className="cap-upload-btn" onClick={() => fileRef.current?.click()}>
                                    {boletaFile ? boletaFile.name : 'Seleccionar archivo (PDF)'}
                                </span>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => setBoletaFile(e.target.files[0] || null)}
                                />
                            </div>
                            {boletaFile && (
                                <small>
                                    <span style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }} onClick={() => { setBoletaFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                                        ✕ Quitar selección
                                    </span>
                                </small>
                            )}
                            {item.boleta_url && !boletaFile && (
                                <small className="text-success mt-1 d-block">
                                    ✓ Ya existe una boleta subida.{' '}
                                    <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(item.boleta_url, '_blank')}>Ver documento</span>
                                </small>
                            )}
                        </FormGroup>

                        <div className="flex justify-end mt-4 pb-3">
                            <Button className="btn-primary px-5 py-2" onClick={handleActualizar} style={{ borderRadius: '8px' }} disabled={isLoading || !canUseActions}>
                                ACTUALIZAR REGISTRO
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalRegistroPagoRhAdministracion;
